import { Box } from '@mui/material';
import RepertoireItem from './RepertoireItem';
import { useSelector, useDispatch } from 'react-redux'
import { RepertoireData, RepertoireReducer } from 'Types/RepertoireTypes';
import { MainAppReducer } from 'Types';
import { useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback, MutableRefObject } from 'react';
import { DragScroll } from './DragScroll'
import { Menu } from 'Components/RoadmapComponents/Menu';
import PageNav from './PageNav';
import SearchBar from 'Components/SearchBar';
import { some, debounce } from 'lodash';
import { CustomCircularProgress } from 'Components/StyledComponents';
import * as repertoireActions from '../../Actions/repertoire';

const Browse = () => {
  const dispatch = useDispatch();
  const appReducerData = useSelector((state: MainAppReducer) => state.mainAppReducer)
  const highestLevelCompleted = appReducerData.highestLevelCompleted

  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer)
  const repertoireData = repertoireReducerData?.repertoireData
  const verticalScrollPosition = repertoireReducerData?.verticalScrollPositions?.browse ?? 0;
  const containerRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
  const hasRestoredScroll = useRef(false);
  const [localRepertoireDataCopy, setLocalRepertoireDataCopy] = useState(repertoireData)
  const [localsearchText, setLocalSearchText] = useState("")

  // Debounced save of vertical scroll position to Redux (only fires 150ms after scrolling stops)
  const debouncedSaveVerticalScroll = useMemo(
    () => debounce((position: number) => {
      dispatch(repertoireActions.setVerticalScroll({ page: 'browse', position }));
    }, 150),
    [dispatch]
  );

  // Flush pending scroll position save on unmount (ensures position is saved before navigating away)
  useEffect(() => {
    return () => debouncedSaveVerticalScroll.flush();
  }, [debouncedSaveVerticalScroll]);

  // Restore vertical scroll position on mount (useLayoutEffect to prevent flash)
  useLayoutEffect(() => {
    if (containerRef.current && !hasRestoredScroll.current && verticalScrollPosition > 0) {
      containerRef.current.scrollTop = verticalScrollPosition;
      hasRestoredScroll.current = true;
    }
  }, [verticalScrollPosition]);

  // Add scroll listener for vertical scroll
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const handleVerticalScroll = () => {
        debouncedSaveVerticalScroll(container.scrollTop);
      };
      container.addEventListener('scroll', handleVerticalScroll);
      return () => container.removeEventListener('scroll', handleVerticalScroll);
    }
  }, [debouncedSaveVerticalScroll]);

  // Sync localRepertoireDataCopy when repertoireData first loads (important for new users on first login)
  useEffect(() => {
    if (repertoireData && !localRepertoireDataCopy) {
      setLocalRepertoireDataCopy(repertoireData)
    }
  }, [repertoireData, localRepertoireDataCopy])


  function removeSpecialCharactersWithAccents(stringWithSpecialChars: string) {
    return stringWithSpecialChars.replace(/[^\p{L}\p{N}\s]/gu, ''); // Keeps letters, numbers, and spaces (including accented characters)
  }

  function replaceAccentedCharacters(stringWithAccents: string) {
    // Mapping of accented characters to their unaccented equivalents
    const accentsMap: {[x:string]:string} = {
      'ř': 'r',
      'á': 'a', 
      'à': 'a',
      'ä': 'a',
      'ã': 'a',
      'â': 'a',
      'ǎ': 'a',
      'ò': 'o',
      'ó': 'o', 
      'ô': 'o',
      'ö': 'o',
      'č': 'c',
      'ě': 'e',
      'é': 'e',
      'ê': 'e',
      'è': 'e',
      'ë': 'e',
      'ú': 'u',
      'ü': 'u',
      'ǔ': 'u',
      'û': 'u',
      'ň': 'n',
      'ñ': 'n',
      'ń': 'n',
      'í': 'i',
      'î': 'i',
      'ì': 'i',
      'ï': 'i',
      'ť': 't',
      'ý': 'y',
      'ŷ': 'y',
      'ÿ': 'y',
      'ź': 'z',
      'ž': 'z',
      'ż': 'z',
    };
  
    return stringWithAccents
      .split('') // Split the string into an array of characters
      .map(char => accentsMap[char] || char) // Replace accented characters or keep the original
      .join('') // Join the characters back into a string
      .replace(/[^a-zA-Z0-9\s]/g, ''); // Remove any remaining special characters
  }

  function normalizeString(unNormalString:string) {
    return removeSpecialCharactersWithAccents(replaceAccentedCharacters(unNormalString.toLocaleLowerCase()))
  }
  

  const filterRepertoire = useCallback((searchText: string)=>{
    const normalizedSearchtext = normalizeString(searchText)
    setLocalSearchText(searchText)
    
    // If repertoireData hasn't loaded yet, set empty array so "No results found" shows
    if (!repertoireData) {
      setLocalRepertoireDataCopy([])
      return
    }
    
    setLocalRepertoireDataCopy(
      repertoireData.filter((rep: RepertoireData) => {
        const searchStringInTitle = normalizeString(rep.name).includes(normalizedSearchtext)
        const searchStringInComposers = some(rep.composers, (composer)=>{
          return normalizeString(composer.name).includes(normalizedSearchtext); 
        })
        const searchStringInArtists = some(rep.artists, (artists)=>{
          return normalizeString(artists.name).includes(normalizedSearchtext); 
        })
        return searchStringInTitle || searchStringInComposers || searchStringInArtists
      })  
    )
  },[repertoireData])

  let repsByLevels: any = []
  for (let i = 0; i <= highestLevelCompleted; i++) {
    repsByLevels.push([])
  }
  let futureRep:any = {}
  repertoireData?.forEach((rep) => {
    const levelNumber = rep.level?.level_number;
    if (levelNumber && levelNumber <= highestLevelCompleted) {
      repsByLevels[levelNumber-1].push(rep)
    } else if (levelNumber) {
      if (futureRep[levelNumber]) {
        futureRep[levelNumber].push(rep)
      } else {
        futureRep[levelNumber] = [rep]
      }
    }
  })
  repsByLevels = repsByLevels.filter((rep: any) => rep.length > 0).reverse()


  return (
    <Box 
      sx={{
        display: 'flex', 
        height: '100vh', 
        width: '100vw',
      }}
    >
      <Menu selected={"Repertoire"}/>
      <Box sx={{display: 'flex', flexDirection: 'column', width: 'calc(100% - 100px)',}}>
        <PageNav selected={"Browse"}/>
        {/* <Box sx={{margin: '0px 0px', justifyContent: 'flex-start', height: 'calc(100vh - 60px)', overflowY: 'auto'}} ref={containerRef}> */}
        <Box sx={{margin: '0px 0px', justifyContent: 'flex-start', height: 'calc(100vh - 100px)', overflowY: 'auto'}} ref={containerRef}>
        {/* <Box sx={{position: 'absolute', top: '73px', right: '20px', zIndex: 2}}> */}
        <Box sx={{position: 'absolute', top: '113px', right: '20px', zIndex: 2}}>
          <SearchBar onChange={filterRepertoire} sx={{ margin: 0 }}/>
        </Box>

      {!repertoireData?.length && 
        <CustomCircularProgress />
      }

      {/* Search results - show independently when searching */}
      { repertoireData?.length && localsearchText !== "" && 
        <>
          <Box sx={{...recForYouStyles, justifyContent: 'space-between', display: 'flex', alignItems: 'center'}}>
            Search Results
          </Box>
          <Box sx={{
            ...containerStyles,
            display: 'flex',
            flexWrap: "wrap",
            padding: '20px',
            justifyContent: 'flex-start',
            gap: '14px',
          }}>
            {localRepertoireDataCopy?.map((rep:any, idx: number) =>
              <RepertoireItem repertoireData={rep} containerRef={containerRef} key={idx}/>
            )}
            {localRepertoireDataCopy?.length === 0 && 
              <Box sx={{ color: '#ADB1B7', fontFamily: 'Lato', fontSize: '16px' }}>
                No results found
              </Box>
            }
          </Box>
        </>
      }
    
      {/* Songs at your level - only show when not searching */}
      { repertoireData?.length && localsearchText === "" && repsByLevels.length > 0 &&
        <>
          <Box sx={{...recForYouStyles, justifyContent: 'space-between', display: 'flex', alignItems: 'center'}}>
            Songs At Your Level
          </Box>
          <Box sx={{margin: '0px 28px', paddingTop: '20px'}}>
            {
            repsByLevels.map((repsByLevel:any, idx:any) => {
              const levelNum = repsByLevel[0]?.level?.level_number || 0;
              return <RepertoireByLevel repsByLevel={repsByLevel} level_number={levelNum} containerRef={containerRef} key={idx} rowId={`browse-level-${levelNum}`}/>
            })
            }
          </Box>
        </>
      }

      { repertoireData?.length && localsearchText === "" && Object.keys(futureRep).length > 0 &&
        <>
        <Box sx={{...recForYouStyles, justifyContent: 'space-between', display: 'flex', alignItems: 'center'}}>
          Songs Above Your Level
        </Box>

        <Box sx={{margin: '0px 28px', paddingTop: '20px'}}>
          {
            Object.keys(futureRep).map((k:any, idx:any) => {
              return <RepertoireByLevel repsByLevel={futureRep[k]} level_number={k} hideTitle={true} containerRef={containerRef} key={idx} rowId={`browse-future-level-${k}`}/>
            })
          }
        </Box>
        </>
      }
      <Box sx={{height: '400px'}}/>
    </Box>
    </Box>
    </Box>
  )
}
export default Browse

const RepertoireByLevel = (props: any) => {
  const { 
    repsByLevel,
    level_number,
    hideTitle,
    containerRef,
    rowId,
  } = props

  if (!repsByLevel || repsByLevel.length == 0) {
    return (<></>)
  }
  
  return (
    <Box sx={containerStyles}>
      <Box sx={titleStyles}>Level {level_number}</Box> 
      <DragScroll rowId={rowId}>
        {repsByLevel.map((rep:any, idx:number) =>
        <RepertoireItem repertoireData={rep} containerRef={containerRef} key={idx}/>
        )}
      </DragScroll>
    </Box>
  )
}

const containerStyles = {
  margin: '4px 0px'
}

const titleStyles = {
  textAlign: 'left',
  fontFamily: 'Lato',
  color: '#ECECEC',
  fontStyle: 'normal',
  fontWeight: '700',
  fontSize: '15px',
  lineHeight: '24px',
}

const recForYouStyles = {
  // had to decrease top/bottom padding here to deal with the search bar size in order to match padding of Home
  padding: '24px 28px',
  backgroundColor: '#171F31',
  color: '#ADB1B7',
  textAlign: 'left',
  fontFamily: 'Lato',
  fontStyle: 'normal',
  fontWeight: '700',
  fontSize: '18px',
}
